<script setup>
import { computed, h, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { picoSearch } from '@scmmishra/pico-search';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

import PageLayout from 'dashboard/components-next/captain/PageLayout.vue';
import SettingsHeader from 'dashboard/components-next/captain/pageComponents/settings/SettingsHeader.vue';
import SuggestedScenarios from 'dashboard/components-next/captain/assistant/SuggestedRules.vue';
import ScenariosCard from 'dashboard/components-next/captain/assistant/ScenariosCard.vue';
import BulkSelectBar from 'dashboard/components-next/captain/assistant/BulkSelectBar.vue';
import AddNewScenariosDialog from 'dashboard/components-next/captain/assistant/AddNewScenariosDialog.vue';

const { t } = useI18n();
const route = useRoute();
const store = useStore();
const { uiSettings, updateUISettings } = useUISettings();
const { formatMessage } = useMessageFormatter();
const assistantId = computed(() => Number(route.params.assistantId));

const uiFlags = useMapGetter('captainScenarios/getUIFlags');
const isFetching = computed(() => uiFlags.value.fetchingList);
const scenarios = useMapGetter('captainScenarios/getRecords');

const searchQuery = ref('');

const LINK_INSTRUCTION_CLASS =
  '[&_a[href^="tool://"]]:text-n-iris-11 [&_a:not([href^="tool://"])]:text-n-slate-12 [&_a]:pointer-events-none [&_a]:cursor-default';

const renderInstruction = instruction => () =>
  h('span', {
    class: `text-sm text-n-slate-12 py-4 prose prose-sm min-w-0 break-words ${LINK_INSTRUCTION_CLASS}`,
    innerHTML: instruction,
  });

// Suggested example scenarios for quick add
const scenariosExample = [
  {
    id: 1,
    title: 'Prospective Buyer',
    description:
      'Handle customers who are showing interest in purchasing a license',
    instruction:
      'If someone is interested in purchasing a license, ask them for following:\n\n1. How many licenses are they willing to purchase?\n2. Are they migrating from another platform?\n. Once these details are collected, do the following steps\n1. add a private note to with the information you collected using [Add Private Note](tool://add_private_note)\n2. Add label "sales" to the contact using [Add Label to Conversation](tool://add_label_to_conversation)\n3. Reply saying "one of us will reach out soon" and provide an estimated timeline for the response and [Handoff to Human](tool://handoff)',
    tools: ['add_private_note', 'add_label_to_conversation', 'handoff'],
  },
];

const filteredScenarios = computed(() => {
  const query = searchQuery.value.trim();
  const source = scenarios.value;
  if (!query) return source;
  return picoSearch(source, query, ['title', 'description', 'instruction']);
});
// Desligado some da IA mas não da tela: fica numa seção recolhida, com o
// interruptor para religar (antes a API só devolvia os ativos).
const activeScenarios = computed(() =>
  filteredScenarios.value.filter(scenario => scenario.enabled !== false)
);
const disabledScenarios = computed(() =>
  filteredScenarios.value.filter(scenario => scenario.enabled === false)
);

const shouldShowSuggestedRules = computed(() => {
  return uiSettings.value?.show_scenarios_suggestions !== false;
});

const closeSuggestedRules = () => {
  updateUISettings({ show_scenarios_suggestions: false });
};

// Bulk selection & hover state
const bulkSelectedIds = ref(new Set());
const hoveredCard = ref(null);

const handleRuleSelect = id => {
  const selected = new Set(bulkSelectedIds.value);
  selected[selected.has(id) ? 'delete' : 'add'](id);
  bulkSelectedIds.value = selected;
};

// Selecionar todos vale só para o que está à vista: o desligado fica na seção
// recolhida e não pode entrar numa exclusão em massa sem a pessoa ver.
const buildSelectedCountLabel = computed(() => {
  const count = activeScenarios.value.length;
  const isAllSelected =
    count > 0 &&
    activeScenarios.value.every(scenario =>
      bulkSelectedIds.value.has(scenario.id)
    );
  return isAllSelected
    ? t('CAPTAIN.ASSISTANTS.SCENARIOS.BULK_ACTION.UNSELECT_ALL', { count })
    : t('CAPTAIN.ASSISTANTS.SCENARIOS.BULK_ACTION.SELECT_ALL', { count });
});

const selectedCountLabel = computed(() => {
  return t('CAPTAIN.ASSISTANTS.SCENARIOS.BULK_ACTION.SELECTED', {
    count: bulkSelectedIds.value.size,
  });
});

const handleRuleHover = (isHovered, id) => {
  hoveredCard.value = isHovered ? id : null;
};

const getToolsFromInstruction = instruction => [
  ...new Set(
    [...(instruction?.matchAll(/\(tool:\/\/([^)]+)\)/g) ?? [])].map(m => m[1])
  ),
];

const toggleScenario = async ({ id, enabled }) => {
  try {
    await store.dispatch('captainScenarios/update', {
      id,
      assistantId: assistantId.value,
      enabled,
    });
    useAlert(
      t(`CAPTAIN.ASSISTANTS.SCENARIOS.TOGGLE.${enabled ? 'ON' : 'OFF'}`)
    );
  } catch {
    useAlert(t('CAPTAIN.ASSISTANTS.SCENARIOS.TOGGLE.ERROR'));
  }
};

const updateScenario = async scenario => {
  try {
    await store.dispatch('captainScenarios/update', {
      id: scenario.id,
      assistantId: assistantId.value,
      ...scenario,
      tools: getToolsFromInstruction(scenario.instruction),
    });
    useAlert(t('CAPTAIN.ASSISTANTS.SCENARIOS.API.UPDATE.SUCCESS'));
  } catch (error) {
    const errorMessage =
      error?.response?.message ||
      t('CAPTAIN.ASSISTANTS.SCENARIOS.API.UPDATE.ERROR');
    useAlert(errorMessage);
  }
};

// Excluir cenário (um ou vários) pede confirmação: card e barra apagavam
// direto. Sem endpoint em massa, cada exclusão é uma chamada; a falha de uma
// não esconde as outras, e só o que foi apagado sai da seleção.
const deleteDialogRef = ref(null);
const pendingDeleteIds = ref([]);
const isDeleting = ref(false);

const requestDelete = ids => {
  if (!ids.length) return;
  pendingDeleteIds.value = ids;
  deleteDialogRef.value?.open();
};

const deleteScenario = id => requestDelete([id]);

const bulkDeleteScenarios = ids =>
  requestDelete(ids || Array.from(bulkSelectedIds.value));

const removeScenarios = async ids => {
  const results = await Promise.allSettled(
    ids.map(id =>
      store.dispatch('captainScenarios/delete', {
        id,
        assistantId: assistantId.value,
      })
    )
  );
  const deleted = ids.filter(
    (_, index) => results[index].status === 'fulfilled'
  );
  const failed = ids.length - deleted.length;
  const selection = new Set(bulkSelectedIds.value);
  deleted.forEach(id => selection.delete(id));
  bulkSelectedIds.value = selection;

  if (!failed) {
    useAlert(t('CAPTAIN.ASSISTANTS.SCENARIOS.API.DELETE.SUCCESS'));
  } else if (deleted.length) {
    useAlert(
      t('CAPTAIN.ASSISTANTS.SCENARIOS.API.DELETE.PARTIAL', {
        deleted: deleted.length,
        failed,
      })
    );
  } else {
    useAlert(t('CAPTAIN.ASSISTANTS.SCENARIOS.API.DELETE.ERROR'));
  }
};

const confirmDelete = async () => {
  if (isDeleting.value) return;
  isDeleting.value = true;
  try {
    await removeScenarios(pendingDeleteIds.value);
  } finally {
    isDeleting.value = false;
    pendingDeleteIds.value = [];
    deleteDialogRef.value?.close();
  }
};

const addScenario = async scenario => {
  try {
    await store.dispatch('captainScenarios/create', {
      assistantId: assistantId.value,
      ...scenario,
      tools: getToolsFromInstruction(scenario.instruction),
    });
    useAlert(t('CAPTAIN.ASSISTANTS.SCENARIOS.API.ADD.SUCCESS'));
  } catch (error) {
    const errorMessage =
      error?.response?.message ||
      t('CAPTAIN.ASSISTANTS.SCENARIOS.API.ADD.ERROR');
    useAlert(errorMessage);
  }
};

const addAllExampleScenarios = async () => {
  try {
    scenariosExample.forEach(async scenario => {
      await store.dispatch('captainScenarios/create', {
        assistantId: assistantId.value,
        ...scenario,
      });
    });
    useAlert(t('CAPTAIN.ASSISTANTS.SCENARIOS.API.ADD.SUCCESS'));
  } catch (error) {
    const errorMessage =
      error?.response?.message ||
      t('CAPTAIN.ASSISTANTS.SCENARIOS.API.ADD.ERROR');
    useAlert(errorMessage);
  }
};

onMounted(() => {
  store.dispatch('captainScenarios/get', {
    assistantId: assistantId.value,
    includeDisabled: true,
  });
  store.dispatch('captainTools/getTools');
});
</script>

<template>
  <PageLayout
    :header-title="$t('CAPTAIN.ASSISTANTS.SCENARIOS.TITLE')"
    :is-fetching="isFetching"
    :show-know-more="false"
    :show-pagination-footer="false"
  >
    <template #body>
      <SettingsHeader
        :heading="$t('CAPTAIN.ASSISTANTS.SCENARIOS.TITLE')"
        :description="$t('CAPTAIN.ASSISTANTS.SCENARIOS.DESCRIPTION')"
      />
      <div v-if="shouldShowSuggestedRules" class="flex mt-7 flex-col gap-4">
        <SuggestedScenarios
          :title="$t('CAPTAIN.ASSISTANTS.SCENARIOS.ADD.SUGGESTED.TITLE')"
          :items="scenariosExample"
          @close="closeSuggestedRules"
          @add="addAllExampleScenarios"
        >
          <template #default="{ item }">
            <div class="flex items-center gap-3 justify-between">
              <span class="text-sm text-n-slate-12">
                {{ item.title }}
              </span>
              <Button
                :label="
                  $t('CAPTAIN.ASSISTANTS.SCENARIOS.ADD.SUGGESTED.ADD_SINGLE')
                "
                ghost
                xs
                slate
                class="!text-sm !text-n-slate-11 flex-shrink-0"
                @click="addScenario(item)"
              />
            </div>
            <div class="flex flex-col">
              <span class="text-sm text-n-slate-11 mt-2">
                {{ item.description }}
              </span>
              <component
                :is="renderInstruction(formatMessage(item.instruction, false))"
              />
              <span class="text-sm text-n-slate-11 font-medium mb-1">
                {{ t('CAPTAIN.ASSISTANTS.SCENARIOS.ADD.SUGGESTED.TOOLS_USED') }}
                {{ item.tools?.map(tool => `@${tool}`).join(', ') }}
              </span>
            </div>
          </template>
        </SuggestedScenarios>
      </div>
      <div class="flex mt-7 flex-col gap-4">
        <div class="flex justify-between items-center">
          <BulkSelectBar
            v-model="bulkSelectedIds"
            :all-items="activeScenarios"
            :select-all-label="buildSelectedCountLabel"
            :selected-count-label="selectedCountLabel"
            :delete-label="
              $t('CAPTAIN.ASSISTANTS.SCENARIOS.BULK_ACTION.BULK_DELETE_BUTTON')
            "
            @bulk-delete="bulkDeleteScenarios"
          >
            <template #default-actions>
              <AddNewScenariosDialog @add="addScenario" />
            </template>
          </BulkSelectBar>
          <div
            v-if="scenarios.length && bulkSelectedIds.size === 0"
            class="max-w-[22.5rem] w-full min-w-0"
          >
            <Input
              v-model="searchQuery"
              :placeholder="
                t('CAPTAIN.ASSISTANTS.SCENARIOS.LIST.SEARCH_PLACEHOLDER')
              "
            />
          </div>
        </div>
        <div v-if="scenarios.length === 0" class="mt-1 mb-2">
          <span class="text-n-slate-11 text-sm">
            {{ t('CAPTAIN.ASSISTANTS.SCENARIOS.EMPTY_MESSAGE') }}
          </span>
        </div>
        <div v-else-if="filteredScenarios.length === 0" class="mt-1 mb-2">
          <span class="text-n-slate-11 text-sm">
            {{ t('CAPTAIN.ASSISTANTS.SCENARIOS.SEARCH_EMPTY_MESSAGE') }}
          </span>
        </div>
        <template v-else>
          <div class="flex flex-col gap-2">
            <ScenariosCard
              v-for="scenario in activeScenarios"
              :id="scenario.id"
              :key="scenario.id"
              :title="scenario.title"
              :description="scenario.description"
              :instruction="scenario.instruction"
              :tools="scenario.tools"
              :enabled="scenario.enabled !== false"
              :is-selected="bulkSelectedIds.has(scenario.id)"
              :selectable="
                hoveredCard === scenario.id || bulkSelectedIds.size > 0
              "
              @select="handleRuleSelect"
              @delete="deleteScenario(scenario.id)"
              @update="updateScenario"
              @toggle="toggleScenario"
              @hover="isHovered => handleRuleHover(isHovered, scenario.id)"
            />
          </div>
          <details
            v-if="disabledScenarios.length"
            class="group rounded-xl border border-n-weak"
            data-testid="scenarios-disabled"
          >
            <summary
              class="flex min-h-11 cursor-pointer list-none items-center gap-2 rounded-xl px-4 text-sm font-medium text-n-slate-12 hover:bg-n-alpha-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
            >
              <span
                class="i-ph-caret-right size-4 transition-transform group-open:rotate-90"
                aria-hidden="true"
              />
              {{
                t('CAPTAIN.ASSISTANTS.SCENARIOS.DISABLED_SECTION', {
                  count: disabledScenarios.length,
                })
              }}
            </summary>
            <div class="flex flex-col gap-2 px-2 pb-2">
              <p class="mx-2 mb-1 text-sm text-n-slate-11">
                {{ t('CAPTAIN.ASSISTANTS.SCENARIOS.DISABLED_HELP') }}
              </p>
              <ScenariosCard
                v-for="scenario in disabledScenarios"
                :id="scenario.id"
                :key="scenario.id"
                :title="scenario.title"
                :description="scenario.description"
                :instruction="scenario.instruction"
                :tools="scenario.tools"
                :enabled="false"
                :is-selected="bulkSelectedIds.has(scenario.id)"
                :selectable="
                  hoveredCard === scenario.id || bulkSelectedIds.size > 0
                "
                @select="handleRuleSelect"
                @delete="deleteScenario(scenario.id)"
                @update="updateScenario"
                @toggle="toggleScenario"
                @hover="isHovered => handleRuleHover(isHovered, scenario.id)"
              />
            </div>
          </details>
        </template>
      </div>
      <Dialog
        ref="deleteDialogRef"
        type="alert"
        :title="t('CAPTAIN.ASSISTANTS.SCENARIOS.DELETE.TITLE')"
        :description="
          t('CAPTAIN.ASSISTANTS.SCENARIOS.DELETE.DESCRIPTION', {
            count: pendingDeleteIds.length,
          })
        "
        :confirm-button-label="t('CAPTAIN.ASSISTANTS.SCENARIOS.DELETE.CONFIRM')"
        :is-loading="isDeleting"
        @confirm="confirmDelete"
      />
    </template>
  </PageLayout>
</template>
